import React from "react";
import styled from "styled-components";

const ISOSection = styled.section`
  padding: 6rem 0;

  background:
    linear-gradient(
      180deg,
      #f8fafc 0%,
      #ffffff 100%
    );

  position: relative;
  overflow: hidden;

  &::before {
    content: '';

    position: absolute;

    top: -200px;
    right: -150px;

    width: 500px;
    height: 500px;

    background:
      radial-gradient(
        circle,
        rgba(66,82,107,0.08),
        transparent 70%
      );

    pointer-events: none;
  }

  /* 1536px+ */
  @media (min-width: 1536px) {
    padding: 7rem 0;

    &::before {
      width: 650px;
      height: 650px;
    }
  }

  /* 1280px */
  @media (max-width: 1280px) {
    padding: 5.5rem 0;
  }

  /* 1024px */
  @media (max-width: 1024px) {
    padding: 5rem 0;

    &::before {
      width: 450px;
      height: 450px;
    }
  }

  /* 768px */
  @media (max-width: 768px) {
    padding: 4rem 0;

    &::before {
      width: 380px;
      height: 380px;
      right: -180px;
    }
  }

  /* 640px */
  @media (max-width: 640px) {
    padding: 3.8rem 0;
  }

  /* 480px */
  @media (max-width: 480px) {
    padding: 3.2rem 0;

    &::before {
      width: 300px;
      height: 300px;
      top: -120px;
    }
  }

  /* 320px */
  @media (max-width: 320px) {
    padding: 2.8rem 0;
  }
`;

const ISOContainer = styled.div`
  max-width: 1280px;

  margin: 0 auto;

  padding: 0 2rem;

  position: relative;
  z-index: 1;

  /* 1536px+ */
  @media (min-width: 1536px) {
    max-width: 1400px;
  }

  /* 1280px */
  @media (max-width: 1280px) {
    max-width: 1180px;
  }

  /* 1024px */
  @media (max-width: 1024px) {
    padding: 0 1.8rem;
  }

  /* 768px */
  @media (max-width: 768px) {
    padding: 0 1.4rem;
  }

  /* 640px */
  @media (max-width: 640px) {
    padding: 0 1.2rem;
  }

  /* 480px */
  @media (max-width: 480px) {
    padding: 0 1rem;
  }

  /* 320px */
  @media (max-width: 320px) {
    padding: 0 0.8rem;
  }
`;

const ISOGrid = styled.div`
  display: grid;

  grid-template-columns:
    repeat(auto-fit, minmax(190px, 1fr));

  gap: 2rem;

  margin-top: 3rem;

  /* 1536px+ */
  @media (min-width: 1536px) {
    gap: 2.4rem;
  }

  /* 1280px */
  @media (max-width: 1280px) {
    grid-template-columns:
      repeat(auto-fit, minmax(180px, 1fr));

    gap: 1.8rem;
  }

  /* 1024px */
  @media (max-width: 1024px) {
    grid-template-columns:
      repeat(auto-fit, minmax(170px, 1fr));

    gap: 1.5rem;
  }

  /* 768px */
  @media (max-width: 768px) {
    grid-template-columns:
      repeat(auto-fit, minmax(160px, 1fr));

    gap: 1.3rem;

    margin-top: 2.5rem;
  }

  /* 640px */
  @media (max-width: 640px) {
    grid-template-columns:
      repeat(2, 1fr);

    gap: 1rem;
  }

  /* 480px */
  @media (max-width: 480px) {
    grid-template-columns:
      repeat(2, 1fr);

    gap: 0.9rem;

    margin-top: 2rem;
  }

  /* 320px */
  @media (max-width: 320px) {
    gap: 0.7rem;
  }
`;

const ISOCard = styled.div`
  background:rgba(215, 215, 144, 0.88);

  border-radius: 24px;

  padding: 1.5rem;

  text-align: center;

  backdrop-filter: blur(12px);

  border:
    1px solid rgba(15,23,42,0.06);

  box-shadow:
    0 8px 30px rgba(15,23,42,0.04);

  transition:
    transform 0.4s ease,
    box-shadow 0.4s ease,
    border 0.4s ease;

  position: relative;

  overflow: hidden;

  &:hover {
    transform: translateY(-8px);

    border-color:
      rgba(66,82,107,0.14);

    box-shadow:
      0 18px 45px rgba(15,23,42,0.08),
      0 8px 24px rgba(66,82,107,0.08);
  }

  /* 1536px+ */
  @media (min-width: 1536px) {
    padding: 1.8rem;
  }

  /* 1280px */
  @media (max-width: 1280px) {
    padding: 1.4rem;
  }

  /* 1024px */
  @media (max-width: 1024px) {
    border-radius: 22px;
  }

  /* 768px */
  @media (max-width: 768px) {
    padding: 1.2rem;

    border-radius: 20px;
  }

  /* 640px */
  @media (max-width: 640px) {
    padding: 1.1rem;
  }

  /* 480px */
  @media (max-width: 480px) {
    padding: 1rem;

    border-radius: 18px;
  }

  /* 320px */
  @media (max-width: 320px) {
    padding: 0.85rem;

    border-radius: 16px;
  }
`;

const ISOImage = styled.div`
  width: 100%;

  height: 130px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 1rem;

  background:
    linear-gradient(
      180deg,
      #FCFAF7 0%,
      #F6F1E8 100%
    );

  border-radius: 18px;

  border:
    1px solid rgba(15,23,42,0.04);

  transition: all 0.35s ease;

  img {
    max-width: 110px;
    max-height: 110px;

    object-fit: contain;

    filter:
      contrast(1.08)
      saturate(1.05)
      drop-shadow(
        0 6px 12px rgba(15,23,42,0.10)
      );

    transition:
      transform 0.35s ease,
      filter 0.35s ease;
  }

  ${ISOCard}:hover & {
    background:
      linear-gradient(
        180deg,
        #ffffff 0%,
        #eef2f7 100%
      );

    img {
      transform: scale(1.06);

      filter:
        contrast(1.12)
        saturate(1.08)
        drop-shadow(
          0 10px 20px rgba(15,23,42,0.14)
        );
    }
  }

  /* 1536px+ */
  @media (min-width: 1536px) {
    height: 150px;

    img {
      max-width: 125px;
      max-height: 125px;
    }
  }

  /* 1280px */
  @media (max-width: 1280px) {
    height: 120px;
  }

  /* 1024px */
  @media (max-width: 1024px) {
    img {
      max-width: 100px;
      max-height: 100px;
    }
  }

  /* 768px */
  @media (max-width: 768px) {
    height: 110px;

    border-radius: 16px;

    img {
      max-width: 90px;
      max-height: 90px;
    }
  }

  /* 640px */
  @media (max-width: 640px) {
    height: 100px;
  }

  /* 480px */
  @media (max-width: 480px) {
    height: 90px;

    border-radius: 14px;

    img {
      max-width: 75px;
      max-height: 75px;
    }
  }

  /* 320px */
  @media (max-width: 320px) {
    height: 80px;

    img {
      max-width: 65px;
      max-height: 65px;
    }
  }
`;

const ISOTitle = styled.h4`
  font-size: 1rem;

  color: #111827;

  font-weight: 600;

  margin-bottom: 0.5rem;

  line-height: 1.4;

  font-family:
    "Cormorant Garamond",
    Georgia,
    serif;

  letter-spacing: 0.3px;

  /* 1536px+ */
  @media (min-width: 1536px) {
    font-size: 1.08rem;
  }

  /* 1280px */
  @media (max-width: 1280px) {
    font-size: 0.98rem;
  }

  /* 1024px */
  @media (max-width: 1024px) {
    line-height: 1.35;
  }

  /* 768px */
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }

  /* 640px */
  @media (max-width: 640px) {
    font-size: 0.92rem;
  }

  /* 480px */
  @media (max-width: 480px) {
    font-size: 0.88rem;
  }

  /* 320px */
  @media (max-width: 320px) {
    font-size: 0.82rem;
  }
`;


const ISODescription = styled.p`
  font-size: 0.82rem;

  color: #6b7280;

  line-height: 1.7;

  margin: 0;

  font-family: "Inter", sans-serif;

  /* 1536px+ */
  @media (min-width: 1536px) {
    font-size: 0.88rem;
  }

  /* 1280px */
  @media (max-width: 1280px) {
    font-size: 0.8rem;
  }

  /* 1024px */
  @media (max-width: 1024px) {
    line-height: 1.65;
  }

  /* 768px */
  @media (max-width: 768px) {
    font-size: 0.78rem;
  }

  /* 640px */
  @media (max-width: 640px) {
    font-size: 0.76rem;
  }

  /* 480px */
  @media (max-width: 480px) {
    font-size: 0.74rem;

    line-height: 1.6;
  }

  /* 320px */
  @media (max-width: 320px) {
    font-size: 0.7rem;
  }
`;

const ViewAllCertificates = styled.div`
  text-align: center;
  margin-top: 3rem;

  .view-all-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.8rem;
    background: linear-gradient(135deg, #D4AF37 0%, #F4E9C1 100%);
    color: #1C2951;
    padding: 1rem 2rem;
    border-radius: 50px;
    font-weight: 600;
    font-size: 1.1rem;
    text-decoration: none;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(212, 175, 55, 0.4);
      background: linear-gradient(135deg, #F4E9C1 0%, #D4AF37 100%);
      text-decoration: none;
      color: #1C2951;
    }

    @media (max-width: 768px) {
      padding: 0.8rem 1.5rem;
      font-size: 1rem;
    }

    @media (max-width: 480px) {
      padding: 0.7rem 1.3rem;
      font-size: 0.95rem;
    }
  }
`;

const VideosSection = styled.section`
  margin-top: 3.5rem;
  padding: 2.5rem 0 1rem;
  background: linear-gradient(180deg, rgba(230,197,127,0.03) 0%, transparent 100%);
  border-radius: 12px;
`;

const VideosContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const VideosHeader = styled.div`
  text-align: center;
  margin-bottom: 1.25rem;

  h3 {
    font-size: 1.5rem;
    color: #1C2951;
    margin: 0 0 0.5rem;
    font-weight: 700;
  }

  p {
    margin: 0;
    color: #64748b;
    font-size: 0.95rem;
  }
`;

const VideosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const VideoCard = styled.div`
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(28,41,81,0.06);
  border: 1px solid rgba(226,232,240,0.6);
`;

const VideoFrameWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 9/16; /* portrait to mimic shorts */
  background: #000;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
`;

const VideoMeta = styled.div`
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .title {
    font-size: 0.95rem;
    color: #1C2951;
    font-weight: 600;
  }

  .play-badge {
    background: linear-gradient(135deg,#D4AF37 0%,#E6C57F 100%);
    color: #122244;
    padding: 0.35rem 0.6rem;
    border-radius: 999px;
    font-weight: 700;
    font-size: 0.8rem;
  }
`;

const ISOCertificates = () => {
  const certificates = [
    {
      image: "/assets/iso_images/ISO-9001-2015.png",
      title: "ISO 9001:2015",
      description: "Quality Management System"
    },
    {
      image: "/assets/iso_images/ISO-14001.png",
      title: "ISO 14001",
      description: "Environmental Management"
    },
    {
      image: "/assets/iso_images/ISO-27001.png",
      title: "ISO 27001",
      description: "Information Security"
    },
    {
      image: "/assets/iso_images/ISO-22000-2018.png",
      title: "ISO 22000:2018",
      description: "Food Safety Management"
    },
    {
      image: "/assets/iso_images/HACCP.png",
      title: "HACCP",
      description: "Hazard Analysis Critical Control"
    },
    {
      image: "/assets/iso_images/ISO-13485-2003.png",
      title: "ISO 13485:2003",
      description: "Medical Devices Quality"
    },
    {
      image: "/assets/iso_images/ISO-50001-2011.png",
      title: "ISO 50001:2011",
      description: "Energy Management System"
    },
    {
      image: "/assets/iso_images/ISO-20000.png",
      title: "ISO 20000",
      description: "IT Service Management"
    },
    {
      image: "/assets/iso_images/ce.png",
      title: "CE Marking",
      description: "European Conformity"
    },
    {
      image: "/assets/iso_images/rohs.png",
      title: "RoHS",
      description: "Restriction of Hazardous Substances"
    },
    {
      image: "/assets/iso_images/halal.png",
      title: "Halal Certification",
      description: "Islamic Dietary Standards"
    },
    {
      image: "/assets/iso_images/organic.png",
      title: "Organic Certification",
      description: "Organic Product Standards"
    }
  ];

  return (
    <ISOSection>
      <ISOContainer>
        <div className="section-header" style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <span className="section-tag" style={{
            display: 'inline-block',
            background: '#E6C57F',
            color: '#1C2951',
            padding: '0.6rem 1.8rem',
            borderRadius: '2rem',
            fontSize: '0.9rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '1rem'
          }}>ISO Certifications</span>
          <h2 className="section-title" style={{
            fontSize: 'clamp(2rem, 5vw, 2.5rem)',
            color: '#1C2951',
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontWeight: '700',
            marginBottom: '1rem',
            lineHeight: '1.2'
          }}>
            Get Your Business <span style={{ color: '#E6C57F' }}>ISO Certified</span>
          </h2>
          <p className="section-description" style={{
            fontSize: '1.1rem',
            color: '#64748b',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            We help businesses achieve international standards compliance with comprehensive
            ISO certification services across various industry domains.
          </p>
        </div>

        <ISOGrid>
          {certificates.map((cert, index) => (
            <ISOCard key={index}>
              <ISOImage>
                <img
                  src={cert.image}
                  alt={cert.title}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </ISOImage>
              <ISOTitle>{cert.title}</ISOTitle>
              <ISODescription>{cert.description}</ISODescription>
            </ISOCard>
          ))}
        </ISOGrid>

        <ViewAllCertificates>
          <a href="/services/iso-certificate" className="view-all-btn">
            Get ISO Certified <i className="fas fa-arrow-right"></i>
          </a>
        </ViewAllCertificates>

        {/* Videos / Shorts Section */}
        <VideosSection aria-labelledby="iso-videos-heading">
          <VideosContainer>
            <VideosHeader>
              <h3 id="iso-videos-heading">Certification Shorts</h3>
              <p>Quick shorts to help you understand our Services, process and success stories — play right here.</p>
            </VideosHeader>

            <VideosGrid>
              {[
                { id: 'tvF30W9nmdU', title: 'Document Attestation' },
                { id: 'QuzbDTV29QY', title: 'LMPC, WPC & EPR Registration ' },
                { id: 'fIiBPay0Kac', title: 'Guide to Trademark Registration ' }
              ].map((v) => (
                <VideoCard key={v.id}>
                  <VideoFrameWrap>
                    <iframe
                      title={v.title}
                      src={`https://www.youtube.com/embed/${v.id}?rel=0&autoplay=0`}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </VideoFrameWrap>
                  <VideoMeta>
                    <div className="title">{v.title}</div>
                    <div className="play-badge">Shorts</div>
                  </VideoMeta>
                </VideoCard>
              ))}
            </VideosGrid>
          </VideosContainer>
        </VideosSection>
      </ISOContainer>
    </ISOSection>
  );
};

export default ISOCertificates;
